import { ReactNode, useEffect } from "react"
import Head from "next/head"
import Image from "next/image"
import { PlacePhoto } from "@/types/place"

import { sendAnalyticsEvent } from "@/lib/analytics/amplitude-utils"
import { isClientSide } from "@/lib/is-client-side"
import { mtLocation } from "@/lib/mt-location"
import { useRouter } from "@/lib/router"
import { RELEASE_VERSION } from "@/lib/version"
import { CreditPopover } from "@/components/credit-popover"
import LogoIcon from "@/components/logo-icon"
import { Button } from "@/components/ui/button"
import { ClientRender } from "./client-render"

export default function ErrorPage({
  code,
  headline = "Unexpected turbulence",
  description,
}: {
  code?: string
  headline?: string
  description?: ReactNode | string
}) {
  const photos: PlacePhoto[] = [
    {
      width: 4752,
      height: 3168,
      additional_information: {
        photographer_name: "Susan Weber",
        photo_link: "https://unsplash.com/photos/hot-air-balloon-jp9Bdu6IGq4",
        photographer_link: "https://unsplash.com/@havasuartist",
        location_name: "Arizona, Lake Havasu City, United States",
      },
      url: "https://cdn.sanity.io/images/cpvxwd28/production/37cd78d8a23fec10a8cb7a39fa06d24215881ff1-4752x3168.jpg",
    },
    {
      width: 6000,
      height: 3375,
      additional_information: {
        photographer_name: "Cédric Dhaenens",
        photo_link:
          "https://unsplash.com/photos/farm-during-daytime-CYQSLIAuzzU",
        photographer_link: "https://unsplash.com/@cedric",
      },
      url: "https://cdn.sanity.io/images/cpvxwd28/production/c754edefafc53a796724a60d74314bc54aa92f6b-6000x3375.jpg",
    },
    {
      width: 4800,
      height: 3200,
      additional_information: {
        photographer_name: "Sandra Seitamaa",
        photo_link:
          "https://unsplash.com/photos/gray-rock-formation-on-sea-shore-during-daytime-dP44CyjA_2M",
        photographer_link: "https://unsplash.com/@seitamaaphotography",
      },
      url: "https://cdn.sanity.io/images/cpvxwd28/production/50146e07d7a17f05837aed9999fb984626675e0a-4800x3200.jpg",
    },
    {
      width: 6000,
      height: 4000,
      additional_information: {
        photographer_name: "Eelco Böhtlingk",
        photo_link:
          "https://unsplash.com/photos/people-in-beach-during-daytime-zrLzLVLZquY",
        photographer_link: "https://unsplash.com/@eelco_bohtlingk",
      },
      url: "https://cdn.sanity.io/images/cpvxwd28/production/e30a2a692c24c4440c4f9dbffbb6d28bc9e6b736-6000x4000.jpg",
    },
    {
      width: 3511,
      height: 1975,
      additional_information: {
        photographer_name: "Derek Oyen",
        photo_link:
          "https://unsplash.com/photos/glacier-during-daytime-4ReskwNsh68",
        photographer_link: "https://unsplash.com/@goosegrease",
      },
      url: "https://cdn.sanity.io/images/cpvxwd28/production/74d45618f4c6c2832548e1cbcc0e1e1213908fcd-3511x1975.jpg",
    },
    {
      width: 3276,
      height: 4095,
      additional_information: {
        photographer_name: "Faisal Waheed",
        photo_link:
          "https://unsplash.com/photos/green-palm-tree-on-water-2i0_OwlZ_K4",
        photographer_link: "https://unsplash.com/@fwaheed17",
      },
      url: "https://cdn.sanity.io/images/cpvxwd28/production/cb567a61e86d874bed60bf5ee12eb0c408bbaaca-3276x4095.jpg",
    },
    {
      width: 2667,
      height: 4000,
      additional_information: {
        photographer_name: "Clay Banks",
        photo_link:
          "https://unsplash.com/photos/brown-mountain-scenery-cLyQC_TCaCQ",
        photographer_link: "https://unsplash.com/@claybanks",
      },
      url: "https://cdn.sanity.io/images/cpvxwd28/production/55019ff425b5f207f95af2798a513d359f3018f4-2667x4000.jpg",
    },
    {
      width: 4016,
      height: 6016,
      additional_information: {
        photographer_name: "Parsa Mahmoudi",
        photo_link:
          "https://unsplash.com/photos/the-night-sky-is-filled-with-stars-above-a-mountain-vLT0J-GbYpU",
        photographer_link: "https://unsplash.com/@parsamphotography",
      },
      url: "https://cdn.sanity.io/images/cpvxwd28/production/1d5e1a98cb799c8c90c5431ccf2fb67832b8be60-4016x6016.jpg",
    },
    {
      width: 7360,
      height: 4912,
      additional_information: {
        photographer_name: "Federico Scarionati",
        photo_link:
          "https://unsplash.com/photos/hot-air-balloon-flying-over-the-clouds-during-sunset-yzgMiy1-5Z8",
        photographer_link: "https://unsplash.com/@federicoscarionati",
      },
      url: "https://cdn.sanity.io/images/cpvxwd28/production/c1bb3d1e6719bbaec13fbe7b2d367518450a4d48-7360x4912.jpg",
    },
    {
      width: 3575,
      height: 4469,
      additional_information: {
        photographer_name: "Gautier Salles",
        photo_link:
          "https://unsplash.com/photos/person-standing-on-rock-cliff-during-daytime-KqajvGvvxJI",
        photographer_link: "https://unsplash.com/@yamnez",
      },
      url: "https://cdn.sanity.io/images/cpvxwd28/production/3873ef66c4362ff6f9fb3c27502b8b99677b94f1-3575x4469.jpg",
    },
    {
      width: 4160,
      height: 3120,
      additional_information: {
        photographer_name: "David Lusvardi",
        photo_link:
          "https://unsplash.com/photos/the-grand-canyon-arizona-nuwkHZT0BoU",
        photographer_link: "https://unsplash.com/@lusvardi",
      },
      url: "https://cdn.sanity.io/images/cpvxwd28/production/957f18b6bd8f7b7b272fe62892692795758c0e4e-4160x3120.jpg",
    },
    {
      width: 5017,
      height: 3345,
      additional_information: {
        photographer_name: "Sina Sadeqi",
        photo_link:
          "https://unsplash.com/photos/silhouette-of-man-riding-bicycle-during-sunset-1b6dpSwupHE",
        photographer_link: "https://unsplash.com/@adventurous_artist",
      },
      url: "https://cdn.sanity.io/images/cpvxwd28/production/13fb6b28d7f79f62d219064731420f0b5a36efb3-5017x3345.jpg",
    },
    {
      width: 3943,
      height: 2700,
      additional_information: {
        photographer_name: "Harshil Gudka",
        photo_link:
          "https://unsplash.com/photos/animal-running-on-field-aKcVSSDotgo",
        photographer_link: "https://unsplash.com/@hgudka97",
      },
      url: "https://cdn.sanity.io/images/cpvxwd28/production/c588f95062906775ca54bd1b5b0e92aae709d25d-3943x2700.jpg",
    },
    {
      width: 2048,
      height: 2560,
      additional_information: {
        photographer_name: "Taneli Lahtinen",
        photo_link:
          "https://unsplash.com/photos/brown-wooden-pathway-RRNHdU2JoKQ",
        photographer_link: "https://unsplash.com/@tanelah",
      },
      url: "https://cdn.sanity.io/images/cpvxwd28/production/46dbaeb6122188a2838916584419bbab62f7849c-2048x2560.jpg",
    },
  ]

  // 404 and 500 pages are re-used and not re-created.  So use the router.asPath to force the useEffect() to run
  const router = useRouter()

  let randomPhoto: PlacePhoto | undefined = undefined
  if (isClientSide()) {
    // Set once
    randomPhoto =
      randomPhoto || photos[Math.floor(Math.random() * photos.length)]
  }

  useEffect(() => {
    console.log("ErrorPage: send analytics event: ", router.asPath)
    sendAnalyticsEvent({
      name: "ErrorPage",
      properties: {
        code: code,
        title: headline,
        description: typeof description === "string" ? description : "",
        version: RELEASE_VERSION,
        // Which URL failed, and (on a full load) where it was linked from.
        path: router.asPath,
        referrer: document.referrer,
      },
    })
    return () => {
      console.log("ErrorPage: useEffect cleanup: ", router.asPath)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath])

  function back() {
    mtLocation.setHref("/")
  }

  function reload() {
    mtLocation.reload()
  }

  // putting this in a separate variable to avoid a React warning
  // "Warning: A title element received an array with more than 1 element as children..."
  const displayHeadline = `${headline} | mindtrip.`

  function renderDescription() {
    if (description == null || typeof description === "string") {
      return (
        <p className="mb-7 text-md text-pretty">
          {description || (
            <>
              Sorry, something went wrong.
              <br />
              Please{" "}
              <button
                onClick={reload}
                type="button"
                className="border-b border-white-a-5 transition-colors hover:border-white-a-8"
              >
                refresh
              </button>{" "}
              the page or click the button below to return home.
            </>
          )}
        </p>
      )
    } else {
      return <div className="mb-7">{description}</div>
    }
  }

  return (
    <>
      <Head>
        <title>{displayHeadline}</title>
      </Head>
      <div className="relative flex min-h-svh flex-col items-center justify-center bg-black px-(--container-pad) py-16 text-center text-white">
        <ClientRender>
          {randomPhoto && (
            <Image
              src={randomPhoto.url}
              alt={randomPhoto.additional_information?.location_name || ""}
              sizes="100vw"
              fill
              className="object-cover object-center transition-opacity duration-500"
              style={{ opacity: 0 }}
              onLoad={(e) => (e.currentTarget.style.opacity = "")}
            />
          )}
        </ClientRender>
        <div className="absolute inset-0 bg-black-a-7" />
        <button
          onClick={back}
          type="button"
          className="absolute top-0 left-0 flex h-(--header-height) w-(--nav-width-collapsed) items-center justify-center"
        >
          <LogoIcon size={24} />
        </button>
        <div className="relative flex flex-col items-center">
          <h1 className="mb-[.125em] text-[clamp(2.5rem,10vw,4rem)] leading-tight font-semibold tracking-tight">
            {headline}
          </h1>
          <div>{renderDescription()}</div>
          <p className="mb-24">
            <Button onClick={back} variant="knockout" size="xl">
              Back to home
            </Button>
          </p>
          {code && (
            <p className="text-4xl font-semibold tracking-tight sm:text-5xl">
              {code}
            </p>
          )}
        </div>
        <ClientRender>
          {randomPhoto && (
            <CreditPopover
              className="absolute right-4 bottom-4"
              credit={{
                name: randomPhoto.additional_information?.photographer_name,
                url: randomPhoto.additional_information?.photographer_link,
              }}
              align="center"
              side="right"
            />
          )}
        </ClientRender>
      </div>
    </>
  )
}
